import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";

// This route exists ONLY for the popup flow (AuthGateModal). It is
// registered as its own separate redirect_uri in the Singpass Developer
// Portal, distinct from the full-page /auth/singpass/callback used by the
// standalone Login page. Because this route is NEVER used for anything
// else, it doesn't need to detect "am I a popup" at all — it always
// broadcasts the result and closes. This sidesteps window.opener (severed
// by Singpass's Cross-Origin-Opener-Policy) and window.name (some browsers
// reset it on cross-origin navigation) entirely, since neither is needed.
export default function SingpassCallbackPopup() {
  const [searchParams] = useSearchParams();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const code = searchParams.get("code");
    const state = searchParams.get("state");

    const sendAndClose = (payload) => {
      const channel = new BroadcastChannel("singpass-auth");
      channel.postMessage(payload);
      // Don't close immediately — postMessage queues the send as an async
      // task, and window.close() can tear down this context before that
      // task is actually flushed, silently dropping the message. A short
      // delay gives it time to actually go out before we destroy the tab.
      setTimeout(() => {
        channel.close();
        window.close();
      }, 200);
    };

    if (!code || !state) {
      sendAndClose({ type: "singpass-auth-complete", error: "Missing Singpass response." });
      return;
    }

    (async () => {
      try {
        const functions = getFunctions();
        const singpassExchange = httpsCallable(functions, "singpassExchange");
        const { data } = await singpassExchange({ code, state });
        sendAndClose({
          type: "singpass-auth-complete",
          customToken: data.customToken,
          profile: data.profile,
        });
      } catch (err) {
        sendAndClose({ type: "singpass-auth-complete", error: "Singpass login failed." });
      }
    })();
  }, [searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#091E1B] text-white">
      <p className="text-white/50">Completing Singpass login…</p>
    </div>
  );
}